import { CommonModule, NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  model,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { ButtonComponent } from '@shared/components/button/button.component';
import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { FileMetadata } from '@shared/services/api.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { PurchaseOrdersService } from '@shared/store/purchase-orders/purchase-orders.service';

import { ActivitiesTabComponent } from './activities-tab/activities-tab.component';
import { AttributesTabComponent } from './attributes-tab/attributes-tab.component';
import {
  BeActivitiesAttributesModalParams,
  BeActivitiesAttributesModalReturnData,
  BeActivitiesAttributesModalRowData,
  BeActivitiesTabState,
  BeActivitiesTabToSave,
  BeAttributesTabState,
  BeAttributesTabToSave,
  InAppDocumentState,
} from './be-activities-attributes-modal.model';
import { BeInlineCategoryDropdownOption } from '../be-inline-category-dropdown/be-inline-category-dropdown.model';

@Component({
  imports: [
    NgSelectModule,
    FormsModule,
    InputComponent,
    ButtonComponent,
    IconComponent,
    AttributesTabComponent,
    ActivitiesTabComponent,
    NgClass,
    CommonModule,
    TooltipDirective,
    FileManagerComponent,
    FileViewerComponent,
  ],
  templateUrl: 'be-activities-attributes-modal.component.html',
})
export class BeActivitiesAttributesModalComponent implements AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private purchaseOrdersService = inject(PurchaseOrdersService);

  loading = signal(false);

  inAppDocumentState = model<InAppDocumentState>({ bucket_keys: [], notes: '' });

  @ViewChild('activitiesTabComponent') activitiesTabComponent!: ActivitiesTabComponent;
  addedColumns: string[] = [];
  @ViewChild('attributesTabComponent') attributesTabComponent!: AttributesTabComponent;
  attributesTabHasUnsavedChanges = false;
  attributesTabState: Record<string, Record<string, string>> = {};
  categories: BeInlineCategoryDropdownOption[] = [];
  deletedColumns: string[] = [];
  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  files: File[] = [];
  gridData$ = new BehaviorSubject<BeActivitiesAttributesModalRowData[]>([]);
  headerNames: Record<string, string> = {};
  isActivitiesTabVisible = true;
  isCategoryListChanged = false;
  isSaving = false;
  metadata: FileMetadata = {};
  notes = '';
  ref =
    inject<
      CustomOverlayRef<BeActivitiesAttributesModalReturnData, BeActivitiesAttributesModalParams>
    >(CustomOverlayRef);
  renamedColumns: { newValue: string; oldValue: string }[] = [];
  selectedVendorId = '';
  showNotesSection = true;
  vendors: { id: string; label: string }[] = [];

  ngAfterViewInit(): void {
    if (this.fileManager) {
      this.fileManager.fileQuery
        .selectAll()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((files) => this.setFiles(files));
    }
  }

  canDeactivate = () => {
    if (this.isSaving) {
      return true;
    }

    if (
      this.activitiesTabComponent.isActivitiesTabHasChanges() ||
      this.attributesTabComponent.isAttributesTabHasChanges() ||
      (this.showNotesSection ? this.notes || this.inAppDocumentState().bucket_keys.length : false)
    ) {
      return this.openUnsavedChanges();
    }

    return true;
  };

  close(): void {
    this.ref.close({
      addedColumns: [],
      deletedColumns: [],
      isCategoryListChanged: false,
      notes: '',
      renamedColumns: [],
      rows: [],
      supporting_document_s3_bucket_keys: [],
      temporaryCategories: [],
      type: 'close',
    });
  }

  constructor() {
    if (!this.ref.data) {
      throw new Error("You didn't pass data to BeActivitiesModal");
    }

    this.selectedVendorId = this.ref.data.vendor.id;
    this.vendors = [this.ref.data.vendor];
    this.ref.canDeactivate = this.canDeactivate;
    this.categories = this.ref.data.categories;
    this.isActivitiesTabVisible = this.ref.data.isActivityTabVisible;
    this.gridData$.next(this.ref.data.rows.map((x) => ({ ...x, attributes: { ...x.attributes } })));
    this.showNotesSection = this.ref.data.showNotesSection;
    this.purchaseOrdersService.get().pipe(takeUntilDestroyed()).subscribe();
  }

  private getAttributesRowData(
    rows: BeActivitiesAttributesModalRowData[]
  ): BeActivitiesAttributesModalRowData[] {
    return rows.map((row: BeActivitiesAttributesModalRowData) => {
      let attributes = row.attributes;
      const obj: Record<string, string> = {};

      Object.entries(this.headerNames).forEach(([key, value]) => {
        obj[value] =
          this.attributesTabState[row.id]?.[key] != null
            ? this.attributesTabState[row.id][key]
            : attributes[key.replace('attributes.', '')] || '';
      });
      attributes = obj;

      return {
        ...row,
        attributes,
        changed: row.changed || !!this.attributesTabState[row.id],
      };
    });
  }

  getBucketKey(bucket_key: string): string {
    const trialId = this.mainQuery.getSelectedTrial()?.id;
    const vendorId = this.selectedVendorId;

    return `trials/${trialId}/vendors/${vendorId}/budget/in-app-budget-edit/${bucket_key}`;
  }

  getFilePath() {
    return () => ``;
  }

  getMetadata(): () => FileMetadata {
    return () => ({});
  }

  noteChange(note: string) {
    this.inAppDocumentState.update((value) => {
      return { bucket_keys: value.bucket_keys, notes: note };
    });
  }

  async openUnsavedChanges(): Promise<boolean> {
    const result = this.overlayService.openUnsavedChangesConfirmation();
    const event = await firstValueFrom(result.afterClosed$);

    return !!event?.data;
  }

  async saveFiles() {
    if (this.showNotesSection && this.files) {
      this.loading.set(true);
      this.files.forEach((file) => {
        this.fileManager?.fileStore.update(file.id, {
          ...file,
          bucket_key: this.getBucketKey(file.bucket_key),
        });
      });
      const metadata: FileMetadata = { admin: '1' };
      const fileSuccess = await this.fileManager?.fileService.uploadFiles({ metadata });

      if (fileSuccess) {
        this.loading.set(false);

        return fileSuccess;
      } else {
        this.overlayService.error('Error uploading files.');
        this.loading.set(false);

        return false;
      }
    }

    return true;
  }

  async saveFromActivitiesTab(data: BeActivitiesTabToSave): Promise<void> {
    this.isSaving = true;
    const success = await this.saveFiles();

    if (success) {
      if (this.attributesTabHasUnsavedChanges) {
        this.ref.close({
          addedColumns: this.addedColumns,
          deletedColumns: this.deletedColumns,
          isCategoryListChanged: data.isCategoryListChanged,
          notes: data.notes,
          renamedColumns: this.renamedColumns,
          rows: this.getAttributesRowData(data.rows),
          supporting_document_s3_bucket_keys: data.supporting_document_s3_bucket_keys,
          temporaryCategories: data.temporaryCategories,
          type: 'save',
        });
      }

      this.ref.close({
        addedColumns: [],
        deletedColumns: [],
        isCategoryListChanged: data.isCategoryListChanged,
        notes: data.notes,
        renamedColumns: [],
        rows: data.rows,
        supporting_document_s3_bucket_keys: data.supporting_document_s3_bucket_keys,
        temporaryCategories: data.temporaryCategories,
        type: 'save',
      });
    }
  }

  async saveFromAttributesTab(data: BeAttributesTabToSave): Promise<void> {
    this.isSaving = true;
    const success = await this.saveFiles();

    if (success) {
      this.ref.close({
        addedColumns: data.addedColumns,
        deletedColumns: data.deletedColumns,
        isCategoryListChanged: this.isCategoryListChanged,
        notes: data.notes,
        renamedColumns: data.renamedColumns,
        rows: data.rows,
        supporting_document_s3_bucket_keys: data.supporting_document_s3_bucket_keys,
        temporaryCategories: this.categories,
        type: 'save',
      });
    }
  }

  setFiles(files: File[]): void {
    const bucket_keys = files.map((file) => {
      return this.getBucketKey(file.bucket_key);
    });

    this.files = files;
    this.inAppDocumentState.set({
      bucket_keys: bucket_keys,
      notes: this.notes,
    });
  }

  toggleToActivitiesTab(attributesTabState: BeAttributesTabState): void {
    this.deletedColumns = attributesTabState.deletedColumns;
    this.renamedColumns = attributesTabState.renamedColumns;
    this.addedColumns = attributesTabState.addedColumns;
    this.attributesTabState = attributesTabState.state;
    this.headerNames = attributesTabState.headerNames;
    this.attributesTabHasUnsavedChanges = attributesTabState.attributesTabHasUnsavedChanges;
    this.isActivitiesTabVisible = true;
  }

  toggleToAttributesTab(activityTabState: BeActivitiesTabState): void {
    this.isCategoryListChanged = activityTabState.isCategoryListChanged;
    this.categories = activityTabState.categories;
    this.isActivitiesTabVisible = false;
  }
}
